import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { OWGameResponse, QuestReset, GetOverwolfXpSubjectEnum, WebsocketEvent } from "@devour/client";
import { IStore } from "@/redux/defaultStore";
import { useQueryClient } from "@tanstack/react-query";
import useWebsocket from "@/hooks/useWebsocket";
import { FaGamepad, FaClock, FaSearch, FaBullseye } from "react-icons/fa";
import FrameOneSwitchInput from "@/components/inputs/FrameOneSwitchInput";
import FrameButton from "@/components/buttons/FrameButton";
import classNames from "classnames";
import OverwolfGoVipQuestRewards from "@/pages/overwolf/components/OverwolfGoVipQuestRewards";
import OverwolfGoVipTimeRewards from "@/pages/overwolf/components/OverwolfGoVipTimeRewards";
import OverwolfGameStatusChip from "@/pages/overwolf/components/OverwolfGameStatusChip";
import useGetOverwolfGameInfo from "@/hooks/useGetOverwolfGameInfo";
import useGetOverwolfGameRewards from "@/hooks/useGetOverwolfGameRewards";
import useGetOverwolfUserStats from "@/hooks/useGetOverwolfUserStats";
import useGetUserOverwolfXP from "@/hooks/useGetUserOverwolfXP";
import { useGetGuilds } from "@/hooks/useGetGuilds";
import { useGetGuildProgress } from "@/hooks/useGetGuildProgress";
import useOverwolfInterop from "@/hooks/useOverwolfInterop";
import GameSearchModal from "@/components/modals/GameSearchModal";
import GameQuestSuggestions from "@/components/games/GameQuestSuggestions";
import LoginModal from "@/components/modals/LoginModal";
import {useGetQuestConfigsForUser} from "@/hooks/useGetQuestConfigsForUser";
import {useGetQuestProgressionForUser} from "@/hooks/useGetQuestProgressionForUser";
import { useGetAllQuestConfigsForUser } from "@/hooks/useGetAllQuestConfigsForUser";

interface GameQuestSectionProps {
    game: OWGameResponse;
    hideSearch?: boolean;
    compact?: boolean;
}

// Enum for view types
enum RewardsView {
    QUESTS = "QUESTS",
    TIME_PLAYED = "TIME_PLAYED",
}

// Filter options for quest reset types
const RESET_FILTERS = [
    {
        title: "Daily",
        mobileTitle: "D",
        resetType: QuestReset.DAILY,
    },
    {
        title: "Weekly",
        mobileTitle: "W",
        resetType: QuestReset.WEEKLY,
    },
    {
        title: "Monthly",
        mobileTitle: "M",
        resetType: QuestReset.MONTHLY,
    },
    {
        title: "All-Time",
        mobileTitle: "All",
        resetType: QuestReset.NONE,
    },
];

const GameQuestSection: React.FC<GameQuestSectionProps> = ({ game, hideSearch = false, compact = false }) => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const isOnOverwolf = useSelector((store: IStore) => store.metaStore.isOnOverwolf);
    const [rewardsView, setRewardsView] = useState<RewardsView>(RewardsView.QUESTS);
    const [showGameSearch, setShowGameSearch] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const queryClient = useQueryClient();

    // Check if we're on mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    // Real-time quest progression updates via WebSocket
    useWebsocket(WebsocketEvent.QUESTPROGRESSED, () => {
        console.log("invalidating quest progression");
        queryClient.invalidateQueries({ queryKey: ["quest-progression-user"] });
        queryClient.invalidateQueries({ queryKey: ["quest-configs-user"] });
    }, true);

    // State for quest filter (only applies to QUESTS view)
    const [selectedResetFilter, setSelectedResetFilter] = useState<QuestReset | undefined>(undefined);

    /*
     * Get the Overwolf game ID from the game data
     * The game.gameId field contains the Overwolf game ID (e.g., "21566" for Marvel Rivals)
     * IMPORTANT: Use gameId for Overwolf API calls (numeric string like "21566")
     */
    const gameId = game.gameId || game.id?.toString() || game._id;

    // Fetch game info to check if events are supported
    const {
        data: gameInfo,
        error: gameInfoError,
    } = useGetOverwolfGameInfo(gameId);

    const {
        data: questConfigs,
        error: questConfigsError,
        isLoading: isQuestConfigLoading,
    } = useGetQuestConfigsForUser(fullToken, {gameId: game?.id});
    const {
        data: questProgressions,
        error: questProgressionsError,
        refetch: refetchQuestProgressions,
    } = useGetQuestProgressionForUser(fullToken, questConfigs?.map(q => q.id).filter(Boolean) || []);

    // Fetch time rewards - ONLY for this specific game
    const {
        data: timeRewardsData,
        isLoading: isLoadingTimeRewards,
        error: timeRewardsError,
    } = useGetUserOverwolfXP(
        fullToken,
        GetOverwolfXpSubjectEnum.TIMEREWARD,
        undefined,
        gameId, // Pass gameId to filter time rewards for this specific game
    );

    // Guild data for quest rewards
    const gameIds = useMemo(() => gameInfo?.id ? [gameInfo.id] : [], [gameInfo?.id]);

    const {
        data: guildsResponse,
        error: guildsError,
    } = useGetGuilds(
        undefined,
        undefined,
        undefined,
        gameIds,
    );

    const {
        data: guildProgress,
        error: guildProgressError,
        refetch: refetchGuildProgress,
    } = useGetGuildProgress(guildsResponse?.guilds?.[0]?.id, fullToken);

    // Set initial filter based on available quests
    useEffect(() => {
        if (questConfigs && questConfigs.length > 0 && !selectedResetFilter) {
            // Find the first filter that has quests
            const countMap = questConfigs.reduce((acc, quest) => {
                acc[quest.questReset] = (acc[quest.questReset] || 0) + 1;
                return acc;
            }, {} as Record<string, number>);

            for (const filter of RESET_FILTERS) {
                const count = countMap[filter.resetType] || 0;
                if (count > 0) {
                    setSelectedResetFilter(filter.resetType);
                    break;
                }
            }
        }
    }, [questConfigs, selectedResetFilter]);

    /*
     * Listen for Overwolf events to update quests in real-time
     * CRITICAL: Only register Overwolf callbacks when actually on Overwolf platform
     */
    useOverwolfInterop(isOnOverwolf ? (ev) => {
        if (ev.type === "ow:guild-progress-updated") {
            void refetchGuildProgress();
        }
    } : undefined);

    // Set default view to QUESTS since we now show suggestions for games without quests
    useEffect(() => {
        // Always default to QUESTS view since we show game suggestions when there are no quests
        setRewardsView(RewardsView.QUESTS);
    }, []);

    // Aggregate errors
    const questsError = gameInfoError || questConfigsError || questProgressionsError || guildsError || guildProgressError;
    const hasTimeRewards = timeRewardsData?.transactions && timeRewardsData.transactions.length > 0;

    // Loading state
    if (isQuestConfigLoading || isLoadingTimeRewards) {
        return (
            <div className="game-quest-section">
                <div className="game-quest-section__header">
                    <h2>
                        <FaGamepad style={{ marginRight: "0.5rem" }} />
                        Mission Tracker
                    </h2>
                </div>
                <div className="game-quest-section__content">
                    <div className="game-quest-section__loading">
                        Loading missions...
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (questsError && timeRewardsError) {
        return (
            <div className="game-quest-section">
                <div className="game-quest-section__header">
                    <h2>
                        <FaGamepad style={{ marginRight: "0.5rem" }} />
                        Mission Tracker
                    </h2>
                </div>
                <div className="game-quest-section__content">
                    <div className="game-quest-section__error">
                        Unable to load mission data. Please try again later.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`game-quest-section ${compact ? "game-quest-section--compact" : ""}`}>
            <div className="game-quest-section__header">
                <h2 className="game-quest-section__title">
                    <FaGamepad /> Mission Tracker
                </h2>
                {/* Game Status Chip - show when in Overwolf */}
                {isOnOverwolf &&
                    <div className="game-quest-section__game-status">
                        <OverwolfGameStatusChip />
                    </div>
                }
                {!hideSearch &&
                    <button
                        className="game-quest-section__link"
                        onClick={() => setShowGameSearch(true)}
                        aria-label="Browse all supported games"
                    >
                        <FaSearch className="game-quest-section__link-icon" />
                        <span className="game-quest-section__link--desktop">All Supported Games</span>
                        <span className="game-quest-section__link--mobile">All Games</span>
                    </button>
                }
            </div>

            <div className="game-quest-section__content">
                {/* Controls container with both filter buttons and toggle */}
                <div className="game-quest-section__controls">
                    {/* Filter buttons - only show when in QUESTS view */}
                    {rewardsView === RewardsView.QUESTS &&
                        <div className="game-quest-section__filters">
                            {RESET_FILTERS.map((resetFilter) =>
                                <FrameButton
                                    size="narrow"
                                    color="gray"
                                    className={classNames("game-quest-section__filter-btn", {
                                        "selected": resetFilter.resetType === selectedResetFilter,
                                    })}
                                    key={resetFilter.resetType}
                                    onClick={() => setSelectedResetFilter(resetFilter.resetType)}
                                >
                                    {isMobile ? resetFilter.mobileTitle : resetFilter.title}
                                </FrameButton>)}
                        </div>
                    }

                    {/* Toggle between Quests and Battle Time */}
                    <FrameOneSwitchInput<RewardsView>
                        className="game-quest-section__toggle"
                        name="quest-rewards-view-toggle"
                        value={rewardsView}
                        onToggle={setRewardsView}
                        options={[
                            {
                                render: <FaBullseye />,
                                value: RewardsView.QUESTS,
                            },
                            {
                                render: <FaClock />,
                                value: RewardsView.TIME_PLAYED,
                            },
                        ]}
                    />
                </div>

                {/* Display content based on selected view */}
                <div className="game-quest-section__rewards">
                    {rewardsView === RewardsView.QUESTS
                        ? questConfigs && questConfigs.length > 0
                            ? <OverwolfGoVipQuestRewards
                                key={`quest-${gameId}`}
                                gameId={gameId}
                                questProgressions={questProgressions}
                                questConfigs={questConfigs || []}
                                guildProgress={guildProgress}
                                selectedResetFilter={selectedResetFilter}
                            />
                            : <div className="game-quest-section__empty">
                                <FaGamepad style={{ fontSize: "2rem", marginBottom: "1rem", opacity: 0.5 }} />
                                <p>No active quests available for this game</p>
                                <span>Try Battle Time mode for time-based rewards!</span>

                                {/* Show game suggestions for games with quests */}
                                <div style={{ marginTop: "1.5rem", width: "100%", overflow: "hidden" }}>
                                    <GameQuestSuggestions currentGameId={gameId} maxGames={6} />
                                </div>
                            </div>

                        : <OverwolfGoVipTimeRewards
                            rewards={timeRewardsData?.transactions || []}
                            gameId={gameId}
                            onLoginClick={() => setShowLoginModal(true)}
                        />
                    }
                </div>
            </div>

            <GameSearchModal
                isOpen={showGameSearch}
                onClose={() => setShowGameSearch(false)}
            />

            <LoginModal
                isOpen={showLoginModal}
                onClose={() => setShowLoginModal(false)}
            />
        </div>
    );
};

export default GameQuestSection;